import produce from 'immer';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';

import {
	Analysis,
	AnalysisV2,
	CompareNumericAnalysisV2,
	CorrelationsAnalysis,
	CrosstabAnalysisV2,
	DensityPlotAnalysisV2,
	ExploreAnalysisV2,
	FrequenciesAnalysisV2,
	KaplanMeierAnalysisV2,
	KaplanMeierDataModels,
	LogisticRegressionAnalysisV2,
	PlotNumericAnalysisV2,
	TimeCourseAnalysisV2,
	TimeWindowSizeType
} from 'api/data/analyses';
import { Variable } from 'api/data/variables';
import { AnalysesById } from 'store/data/analyses';
import { VariablesData } from 'store/data/variables';
import { GenericMap } from 'types/index';

import { resetAnalysisInputOutput } from './resetAnalysisInputOutput';

import { AnalysisType } from 'api/data/analyses/constants';
import {
	buildVariablesRichData,
	getAggregatorVariableNameByAggregationRuleName,
	variablesDataArrayIterator
} from 'helpers/variables';
import { VariableType } from 'types/data/variables/constants';

type AnalysisInputVariables = {
	key: string;
	variableRef: string;
	variableRefType: VariableType | VariableType[];
}[];

interface Props {
	analyses: Analysis[];
	analysesById: AnalysesById;
	variablesData: VariablesData;
}

export function rebuildInvalidAnalyses({ analyses, analysesById, variablesData }: Props) {
	const newAnalysesById = cloneDeep(analysesById);

	const { variablesDataArray, variablesMap, variableSetsMap } =
		buildVariablesRichData(variablesData);

	const aggregatorVariableNameByAggregationRuleName =
		getAggregatorVariableNameByAggregationRuleName(variableSetsMap);

	const numericVariableNames: string[] = [];
	const categoryVariableNames: string[] = [];
	const categoryMultipleVariableNames: string[] = [];
	const dateVariableNames: string[] = [];
	const durationVariableNames: string[] = [];

	const variablesFromSet: GenericMap<{
		numericVariableNames: string[];
		categoryVariableNames: string[];
		categoryMultipleVariableNames: string[];
	}> = {};

	// BUILD VARIABLES BY TYPE ARRAYS
	variablesDataArrayIterator(
		variablesDataArray,
		// VARIABLE
		variable => addVariableToNamesArray(variable),
		// GROUP
		groupData => {
			const { groupVariables } = groupData;

			groupVariables.forEach(variable => addVariableToNamesArray(variable));
		},
		// VARIABLE SET
		variableSetData => {
			const { aggregationRules, setData, setName } = variableSetData;

			variablesDataArrayIterator(
				setData,
				variable => addVariableToNamesArray(variable, undefined, setName),
				groupData => {
					const { groupVariables } = groupData;

					groupVariables.forEach(variable =>
						addVariableToNamesArray(variable, undefined, setName)
					);
				},
				() => null
			);

			aggregationRules.forEach(aggregationRule => {
				const variable = variablesMap[aggregationRule.aggregator.variableName];

				addVariableToNamesArray(variable, aggregationRule.name);
			});
		}
	);

	function addVariableToNamesArray(variable: Variable, customName?: string, setName?: string) {
		const isNumericVariable = [VariableType.Integer, VariableType.Float].includes(
			variable.type
		);

		const isDurationVariable = VariableType.TimeDuration === variable.type;
		const isCategoryVariable = VariableType.Category === variable.type;
		const isCategoryMultipleVariable = VariableType.CategoryMultiple === variable.type;
		const isDateVariable = [VariableType.Date, VariableType.DateTime].includes(variable.type);

		if (setName) {
			if (variablesFromSet[setName]) {
				if (isNumericVariable)
					variablesFromSet[setName].numericVariableNames.push(variable.name);
				if (isCategoryVariable)
					variablesFromSet[setName].categoryVariableNames.push(variable.name);
				if (isCategoryMultipleVariable)
					variablesFromSet[setName].categoryMultipleVariableNames.push(variable.name);
			} else {
				if (isNumericVariable)
					variablesFromSet[setName] = {
						numericVariableNames: [variable.name],
						categoryMultipleVariableNames: [],
						categoryVariableNames: []
					};
				if (isDateVariable)
					variablesFromSet[setName] = {
						numericVariableNames: [],
						categoryMultipleVariableNames: [],
						categoryVariableNames: []
					};
				if (isCategoryVariable)
					variablesFromSet[setName] = {
						numericVariableNames: [],
						categoryMultipleVariableNames: [],
						categoryVariableNames: [variable.name]
					};
			}
		}

		if (isNumericVariable) numericVariableNames.push(customName ?? variable.name);
		if (isCategoryVariable) categoryVariableNames.push(customName ?? variable.name);
		if (isCategoryMultipleVariable)
			categoryMultipleVariableNames.push(customName ?? variable.name);
		if (isDateVariable) dateVariableNames.push(customName ?? variable.name);
		if (isDurationVariable) durationVariableNames.push(customName ?? variable.name);
	}

	analyses.forEach(analysis => {
		// FREQUENCIES
		if (isType(AnalysisType.FrequenciesV2)) {
			analysis = analysis as FrequenciesAnalysisV2;

			const { categoryVariable } = analysis.input.variables;

			const inputVariableTypes = analysisInputVariableTypes[AnalysisType.FrequenciesV2];

			const analysisInputVariables: AnalysisInputVariables = [
				{
					key: 'categoryVariable',
					variableRef: categoryVariable?.name ?? '',
					variableRefType: inputVariableTypes.categoryVariable
				}
			];

			const validAnalysis = validateAnalysisInputVariables(analysisInputVariables, analysis);

			const hasChanges = !isEqual(analysis, validAnalysis);

			if (hasChanges) newAnalysesById[analysis.id] = resetAnalysisInputOutput(validAnalysis);
		}

		// EXPLORE
		if (isType(AnalysisType.ExploreV2)) {
			analysis = analysis as ExploreAnalysisV2;

			const variableRefs = analysis.input.variables.map(item => item.name);

			const inputTypes = analysisInputVariableTypes[AnalysisType.ExploreV2];

			const analysisInputVariables: AnalysisInputVariables = variableRefs.map(
				variableRef => ({
					key: '',
					variableRef,
					variableRefType: inputTypes
				})
			);

			const newVariableRefs: string[] = [];

			analysisInputVariables.forEach(({ variableRef, variableRefType }) => {
				if (
					hasVariableRef(variableRef) &&
					isVariableRefValid(variableRef, variableRefType)
				) {
					newVariableRefs.push(variableRef);
				}
			});

			// REBUILD IF NO VARIABLE REFS
			if (newVariableRefs.length === 0) {
				const defaultVariableRef = getDefaultVariableRef(inputTypes);

				newVariableRefs.push(defaultVariableRef);
			}

			// APPLY NEW VARIABLE REFS
			const validAnalysis = produce(analysis, draft => {
				draft.input.variables = newVariableRefs.map(variableRef => ({
					name: variableRef
				}));
			});

			const hasChanges = !isEqual(analysis, validAnalysis);

			if (hasChanges) newAnalysesById[analysis.id] = resetAnalysisInputOutput(validAnalysis);
		}

		// COMPARE NUMERIC
		if (isType(AnalysisType.CompareNumericV2)) {
			analysis = analysis as CompareNumericAnalysisV2;

			const { categoryVariable, exploreVariable, categoryVariableTwo, exploreVariableTwo } =
				analysis.input.variables;

			const inputVariableTypes = analysisInputVariableTypes[AnalysisType.CompareNumericV2];

			const analysisInputVariables: AnalysisInputVariables = [
				{
					key: 'categoryVariable',
					variableRef: categoryVariable?.name ?? '',
					variableRefType: inputVariableTypes.categoryVariable
				},
				{
					key: 'exploreVariable',
					variableRef: exploreVariable?.name ?? '',
					variableRefType: inputVariableTypes.exploreVariable
				}
			];

			if (categoryVariableTwo) {
				analysisInputVariables.push({
					key: 'categoryVariableTwo',
					variableRef: categoryVariableTwo?.name ?? '',
					variableRefType: inputVariableTypes.categoryVariableTwo
				});
			}

			if (exploreVariableTwo) {
				analysisInputVariables.push({
					key: 'exploreVariableTwo',
					variableRef: exploreVariableTwo?.name ?? '',
					variableRefType: inputVariableTypes.exploreVariableTwo
				});
			}

			const validAnalysis = validateAnalysisInputVariables(analysisInputVariables, analysis);

			const hasChanges = !isEqual(analysis, validAnalysis);

			if (hasChanges) newAnalysesById[analysis.id] = resetAnalysisInputOutput(validAnalysis);
		}

		// CROSSTAB
		if (isType(AnalysisType.CrosstabV2)) {
			analysis = analysis as CrosstabAnalysisV2;

			const { rowVariable, columnVariable } = analysis.input.variables;

			const inputVariableTypes = analysisInputVariableTypes[AnalysisType.CrosstabV2];

			const analysisInputVariables: AnalysisInputVariables = [
				{
					key: 'rowVariable',
					variableRef: rowVariable?.name ?? '',
					variableRefType: inputVariableTypes.rowVariable
				},
				{
					key: 'columnVariable',
					variableRef: columnVariable?.name ?? '',
					variableRefType: inputVariableTypes.columnVariable
				}
			];

			const validAnalysis = validateAnalysisInputVariables(analysisInputVariables, analysis);

			const hasChanges = !isEqual(analysis, validAnalysis);

			if (hasChanges) newAnalysesById[analysis.id] = resetAnalysisInputOutput(validAnalysis);
		}

		// KAPLAN MEIER
		if (isType(AnalysisType.KaplanV2)) {
			analysis = analysis as KaplanMeierAnalysisV2;

			const { durationVariable, eventVariable, startDate, endDate, groupVariable } =
				analysis.input.variables;

			const { selectedDataModel } = analysis.input;

			const inputVariableTypes = analysisInputVariableTypes[AnalysisType.Kaplan];

			const analysisInputVariables: AnalysisInputVariables = [
				{
					key: 'durationVariable',
					variableRef: durationVariable?.name ?? '',
					variableRefType: inputVariableTypes.durationVariable
				},
				{
					key: 'eventVariable',
					variableRef: eventVariable?.name ?? '',
					variableRefType: inputVariableTypes.eventVariable
				},
				{
					key: 'startDate',
					variableRef: startDate?.name ?? '',
					variableRefType: inputVariableTypes.startDate
				},
				{
					key: 'endDate',
					variableRef: endDate?.name ?? '',
					variableRefType: inputVariableTypes.endDate
				}
			];

			if (groupVariable) {
				analysisInputVariables.push({
					key: 'groupVariable',
					variableRef: groupVariable.name,
					variableRefType: inputVariableTypes.groupVariable
				});
			}

			const validAnalysis = cloneDeep(
				validateAnalysisInputVariables(analysisInputVariables, analysis)
			) as KaplanMeierAnalysisV2;

			const wasEventVariableValid = eventVariable;
			const isEventVariableValid = validAnalysis.input.variables.eventVariable;

			/**
			 * RESET VARIABLE REF `eventVariable` CATEGORY VALUE `positiveEvent`
			 * IF VARIABLE REF GOT DELETED
			 */
			if (wasEventVariableValid && !isEventVariableValid) {
				validAnalysis.input.variables.positiveEvent[0] = '';
			}

			// UPDATE `positiveEvent` FROM THE NEW VARIABLE REF ASSIGNED
			if (isEventVariableValid) {
				const variable =
					variablesMap[validAnalysis.input.variables.eventVariable?.name ?? ''];

				const defaultCategoryValue = variable.categories[0]?.value ?? '';

				validAnalysis.input.variables.positiveEvent[0] = defaultCategoryValue;
			}

			const startDateEndDateSameVariableRef =
				validAnalysis.input.variables.startDate &&
				validAnalysis.input.variables.endDate &&
				validAnalysis.input.variables.startDate === validAnalysis.input.variables.endDate;

			// INVALIDATE `endDate` IF `endDate` and `startDate` VARIABLE REFS ARE THE SAME
			if (startDateEndDateSameVariableRef) {
				validAnalysis.input.variables.endDate;
			}

			const isStartDateValid = validAnalysis.input.variables.startDate;
			const isEndDateValid = validAnalysis.input.variables.endDate;
			const isFromAndEndDateValid = isStartDateValid && isEndDateValid;

			const isTimeVariableOption = selectedDataModel === KaplanMeierDataModels.duration;
			const isTimeRangeOption =
				selectedDataModel === KaplanMeierDataModels.timeRangeWithEvent;

			// INVALIDATE `selectedTimeOption.RANGE` IF `startDate` AND `endDate` ARE NOT BOTH VALID
			if (isTimeRangeOption && !isFromAndEndDateValid) {
				validAnalysis.input.selectedDataModel = KaplanMeierDataModels.duration;
			}

			const wasDurationVariableValid = durationVariable;
			const isDurationVariableValid = validAnalysis.input.variables.durationVariable;
			const durationVariableBecameInvalid =
				wasDurationVariableValid && !isDurationVariableValid;

			/**
			 * SET `selectedTimeOption.VARIABLE` TO `selectedTimeOption.RANGE` IF
			 * `durationVariable` BECAME INVALID AND
			 * `startDate` AND `endDate` ARE VALID
			 */
			if (isTimeVariableOption && durationVariableBecameInvalid && isFromAndEndDateValid) {
				validAnalysis.input.selectedDataModel = KaplanMeierDataModels.timeRangeWithEvent;
			}

			const hasChanges = !isEqual(analysis, validAnalysis);

			if (hasChanges) newAnalysesById[analysis.id] = resetAnalysisInputOutput(validAnalysis);
		}

		// CORRELATIONS
		if (isType(AnalysisType.CorrelationsV2)) {
			analysis = analysis as CorrelationsAnalysis;

			const { xVariable, yVariable, groupVariables } = analysis.input.variables;

			const inputVariableTypes = analysisInputVariableTypes[AnalysisType.CorrelationsV2];

			const analysisInputVariables: AnalysisInputVariables = [
				{
					key: 'xVariable',
					variableRef: xVariable?.name ?? '',
					variableRefType: inputVariableTypes.xVariable
				},
				{
					key: 'yVariable',
					variableRef: yVariable?.name ?? '',
					variableRefType: inputVariableTypes.yVariable
				}
			];

			if (groupVariables.length > 0) {
				analysisInputVariables.push({
					key: 'groupVariables',
					variableRef: groupVariables[0].name,
					variableRefType: inputVariableTypes.groupVariables
				});
			}

			const validAnalysis = validateAnalysisInputVariables(analysisInputVariables, analysis);

			const hasChanges = !isEqual(analysis, validAnalysis);

			if (hasChanges) newAnalysesById[analysis.id] = resetAnalysisInputOutput(validAnalysis);
		}

		if (isType(AnalysisType.PlotNumericV2)) {
			analysis = analysis as PlotNumericAnalysisV2;

			const { categoryVariable, numericVariable, groupingVariable } =
				analysis.input.variables;

			const inputVariableTypes = analysisInputVariableTypes[AnalysisType.PlotNumericV2];

			const analysisInputVariables: AnalysisInputVariables = [
				{
					key: 'categoryVariable',
					variableRef: categoryVariable?.name ?? '',
					variableRefType: inputVariableTypes.categoryVariable
				},
				{
					key: 'numericVariable',
					variableRef: numericVariable?.name ?? '',
					variableRefType: inputVariableTypes.numericVariable
				}
			];

			if (groupingVariable) {
				analysisInputVariables.push({
					key: 'groupingVariable',
					variableRef: groupingVariable?.name ?? '',
					variableRefType: inputVariableTypes.groupingVariable
				});
			}

			const validAnalysis = validateAnalysisInputVariables(analysisInputVariables, analysis);

			const hasChanges = !isEqual(analysis, validAnalysis);

			if (hasChanges) newAnalysesById[analysis.id] = resetAnalysisInputOutput(validAnalysis);
		}

		// DENSITY PLOT
		if (isType(AnalysisType.DensityPlotV2)) {
			analysis = analysis as DensityPlotAnalysisV2;

			const { numericVariable, groupVariables } = analysis.input.variables;

			const inputVariableTypes = analysisInputVariableTypes[AnalysisType.DensityPlotV2];

			const analysisInputVariables: AnalysisInputVariables = [
				{
					key: 'numericVariable',
					variableRef: numericVariable?.name ?? '',
					variableRefType: inputVariableTypes.numericVariable
				}
			];

			if (groupVariables) {
				analysisInputVariables.push({
					key: 'groupVariables',
					variableRef: groupVariables?.[0].name ?? '',
					variableRefType: inputVariableTypes.groupVariables
				});
			}

			const validAnalysis = validateAnalysisInputVariables(analysisInputVariables, analysis);

			const hasChanges = !isEqual(analysis, validAnalysis);

			if (hasChanges) newAnalysesById[analysis.id] = resetAnalysisInputOutput(validAnalysis);
		}

		// TIME COURSE
		if (isType(AnalysisType.TimeCourseV2)) {
			analysis = analysis as TimeCourseAnalysisV2;

			const { numericVariable, timeVariable, timeUnit, groupVariables } =
				analysis.input.variables;

			const inputVariableTypes = analysisInputVariableTypes[AnalysisType.TimeCourseV2];

			const analysisInputVariables: AnalysisInputVariables = [
				{
					key: 'numericVariable',
					variableRef: numericVariable?.name ?? '',
					variableRefType: inputVariableTypes.numericVariable
				},
				{
					key: 'timeVariable',
					variableRef: timeVariable?.name ?? '',
					variableRefType: inputVariableTypes.timeVariable
				}
			];

			analysisInputVariables.push({
				key: 'groupVariables',
				variableRef: groupVariables?.[0]?.name ?? '',
				variableRefType: inputVariableTypes.groupVariables
			});

			const validAnalysis = validateAnalysisInputVariables(
				analysisInputVariables,
				analysis
			) as TimeCourseAnalysisV2;

			const validAnalysisInputVariables = validAnalysis.input.variables;
			const hasDateRef =
				validAnalysisInputVariables.timeVariable &&
				hasVariableRef(validAnalysisInputVariables.timeVariable?.name);

			if (hasDateRef) {
				const variable = validAnalysisInputVariables.timeVariable
					? variablesMap[validAnalysisInputVariables.timeVariable?.name]
					: null;
				const isDate = variable?.type === VariableType.Date;

				const DATE_TIME_WINDOW_VALUES = [
					TimeWindowSizeType.Hours,
					TimeWindowSizeType.Minutes,
					TimeWindowSizeType.Seconds
				];

				const shouldResetTimeWindow =
					isDate && timeUnit && DATE_TIME_WINDOW_VALUES.includes(timeUnit);

				if (shouldResetTimeWindow) {
					validAnalysisInputVariables.timeUnit = TimeWindowSizeType.Days;
				}
			}

			const hasChanges = !isEqual(analysis, validAnalysis);

			if (hasChanges) newAnalysesById[analysis.id] = resetAnalysisInputOutput(validAnalysis);
		}

		// LOGISTIC REGRESSION
		if (isType(AnalysisType.LogisticRegressionV2)) {
			analysis = analysis as LogisticRegressionAnalysisV2;

			const { xVariable, yVariable, outcomes } = analysis.input.variables;

			const inputVariableTypes =
				analysisInputVariableTypes[AnalysisType.LogisticRegressionV2];

			const analysisInputVariables: AnalysisInputVariables = [
				{
					key: 'xVariable',
					variableRef: xVariable?.name ?? '',
					variableRefType: inputVariableTypes.xVariable
				},
				{
					key: 'yVariable',
					variableRef: yVariable?.name ?? '',
					variableRefType: inputVariableTypes.yVariable
				}
			];

			const validAnalysis = cloneDeep(
				validateAnalysisInputVariables(analysisInputVariables, analysis)
			) as LogisticRegressionAnalysisV2;

			let categories: string[] = [];

			let variable = variablesMap[yVariable?.name ?? ''];
			if (yVariable && yVariable.name in aggregatorVariableNameByAggregationRuleName) {
				const aggregatorVariableName =
					aggregatorVariableNameByAggregationRuleName[yVariable.name];

				variable = variablesMap[aggregatorVariableName];
			}

			if (variable) {
				categories = variable.categories.map(value => value.value);
			}

			const finalCategories: string[] = [];

			if (outcomes.length) {
				outcomes.forEach(value => {
					if (categories.includes(value)) {
						finalCategories.push(value);
					}
				});
			} else {
				if (categories.length) {
					finalCategories.push(categories[0]);
				}
			}

			validAnalysis.input.variables.outcomes = finalCategories;

			const initialAnalysis = newAnalysesById[analysis.id];
			const hasChanges = !isEqual(initialAnalysis, validAnalysis);

			if (hasChanges) newAnalysesById[analysis.id] = resetAnalysisInputOutput(validAnalysis);
		}

		function isType(analysisType: AnalysisType): boolean {
			return analysis.type === analysisType;
		}
	});

	/**
	 * Checks if `variableRef` exists and if `variable.type` corresponds to analysis `variableRefType` type.
	 *
	 * @param variableRef variable name
	 * @param variableRefType
	 *
	 * @returns boolean
	 */
	function isVariableRefValid(
		variableRef: string,
		variableRefType: VariableType | VariableType[]
	): boolean {
		let variable = variablesMap[variableRef];

		if (variableRef in aggregatorVariableNameByAggregationRuleName) {
			const aggregatorVariableName = aggregatorVariableNameByAggregationRuleName[variableRef];

			variable = variablesMap[aggregatorVariableName];
		}

		// VARIABLE GOT DELETED - INVALID
		if (!variable) return false;

		if (Array.isArray(variableRefType)) {
			const variableRefTypes = variableRefType;

			return variableRefTypes.includes(variable.type);
		}

		return variable.type === variableRefType;
	}

	/**
	 *
	 * @param variableRefType variable name
	 * @param second if `true` returns the 2nd variable name; default: returns 1st
	 *
	 * @returns variable name or empty string if not found
	 */
	function getDefaultVariableRef(
		variableRefType: VariableType | VariableType[],
		options: {
			second?: boolean;
			fallbackToFirst?: boolean;
		} = {
			second: false,
			fallbackToFirst: true
		}
	): string {
		let defaultVariableRef = '';

		const isDurationVariable = VariableType.TimeDuration === variableRefType;

		const isNumericVariable = Array.isArray(variableRefType)
			? variableRefType.some(type =>
					[VariableType.Integer, VariableType.Float].includes(type)
			  )
			: [VariableType.Integer, VariableType.Float].includes(variableRefType);
		const isCategoryVariable = Array.isArray(variableRefType)
			? variableRefType.includes(VariableType.Category)
			: VariableType.Category === variableRefType;
		const isCategoryMultipleVariable = Array.isArray(variableRefType)
			? variableRefType.includes(VariableType.CategoryMultiple)
			: VariableType.CategoryMultiple === variableRefType;
		const isDateVariable = Array.isArray(variableRefType)
			? variableRefType.some(type =>
					[VariableType.Date, VariableType.DateTime].includes(type)
			  )
			: [VariableType.Date, VariableType.DateTime].includes(variableRefType);

		const { second, fallbackToFirst } = options;

		if (isNumericVariable) {
			if (second) {
				if (fallbackToFirst) {
					defaultVariableRef = numericVariableNames[1] ?? numericVariableNames[0] ?? '';
				} else {
					defaultVariableRef = numericVariableNames[1] ?? '';
				}
			} else {
				defaultVariableRef = numericVariableNames[0] ?? '';
			}
		} else if (isCategoryVariable) {
			if (second) {
				if (fallbackToFirst) {
					defaultVariableRef = categoryVariableNames[1] ?? categoryVariableNames[0] ?? '';
				} else {
					defaultVariableRef = categoryVariableNames[1] ?? '';
				}
			} else {
				defaultVariableRef = categoryVariableNames[0] ?? '';
			}
		} else if (isCategoryMultipleVariable) {
			if (second) {
				if (fallbackToFirst) {
					defaultVariableRef = categoryVariableNames[1] ?? categoryVariableNames[0] ?? '';
				} else {
					defaultVariableRef = categoryVariableNames[1] ?? '';
				}
			} else {
				defaultVariableRef = categoryVariableNames[0] ?? '';
			}
		} else if (isDateVariable) {
			if (second) {
				if (fallbackToFirst) {
					defaultVariableRef = dateVariableNames[1] ?? dateVariableNames[0] ?? '';
				} else {
					defaultVariableRef = dateVariableNames[1] ?? '';
				}
			} else {
				defaultVariableRef = dateVariableNames[0] ?? '';
			}
		} else if (isDurationVariable) {
			if (second) {
				if (fallbackToFirst) {
					defaultVariableRef = durationVariableNames[1] ?? durationVariableNames[0] ?? '';
				} else {
					defaultVariableRef = durationVariableNames[1] ?? '';
				}
			} else {
				defaultVariableRef = durationVariableNames[0] ?? '';
			}
		}

		return defaultVariableRef;
	}

	/**
	 * - invalidates input variable refs if variable was deleted or if `variable.type` changed
	 * and doesn't correspond to analysis input variable ref type
	 * - validates
	 *
	 * @param analysisInputVariables
	 * @param analysis analysis to validate
	 */
	function validateAnalysisInputVariables(
		analysisInputVariables: AnalysisInputVariables,
		analysis: AnalysisV2
	): AnalysisV2 {
		return produce(analysis, draft => {
			analysisInputVariables.forEach(({ key, variableRef, variableRefType }) => {
				/**
				 * INVALIDATE VARIABLE REF IF:
				 * - VARIABLE DOES NOT EXIST
				 * - VARIABLE TYPE HAS CHANGED
				 */
				if (
					hasVariableRef(variableRef) &&
					!isVariableRefValid(variableRef, variableRefType)
				) {
					// @ts-ignore
					delete draft.input.variables[key];
				}
			});
		});
	}

	const shouldRebuildAnalyses = !isEqual(analysesById, newAnalysesById);

	const output = {
		shouldRebuildAnalyses,
		newAnalysesById
	};

	return output;
}

function hasVariableRef(variableRef: string): boolean {
	return variableRef !== '';
}

/**
 * RETURNS THE VARIABLE TYPE OF EACH ANALYSIS VARIABLE INPUT BY ANALYSIS TYPE
 */
export const analysisInputVariableTypes = {
	[AnalysisType.Frequencies]: {
		categoryVariable: VariableType.Category
	},
	[AnalysisType.FrequenciesV2]: {
		categoryVariable: [VariableType.Category, VariableType.CategoryMultiple]
	},
	[AnalysisType.Explore]: [VariableType.Integer, VariableType.Float, VariableType.TimeDuration],
	[AnalysisType.ExploreV2]: [VariableType.Integer, VariableType.Float, VariableType.TimeDuration],
	[AnalysisType.CompareNumericV1]: {
		categoryVariable: VariableType.Category,
		exploreVariable: [VariableType.Integer, VariableType.Float],
		categoryVariableTwo: VariableType.Category,
		exploreVariableTwo: [VariableType.Integer, VariableType.Float]
	},
	[AnalysisType.CompareNumericV2]: {
		categoryVariable: VariableType.Category,
		exploreVariable: [VariableType.Integer, VariableType.Float],
		categoryVariableTwo: VariableType.Category,
		exploreVariableTwo: [VariableType.Integer, VariableType.Float]
	},
	[AnalysisType.Crosstab]: {
		yVariable: VariableType.Category,
		groupingVariable: VariableType.Category
	},
	[AnalysisType.CrosstabV2]: {
		rowVariable: VariableType.Category,
		columnVariable: VariableType.Category
	},
	[AnalysisType.Kaplan]: {
		durationVariable: [VariableType.Integer, VariableType.Float],
		eventVariable: VariableType.Category,
		startDate: [VariableType.Date, VariableType.DateTime],
		endDate: [VariableType.Date, VariableType.DateTime],
		groupVariable: VariableType.Category
	},
	[AnalysisType.CorrelationsV1]: {
		xNumericVariable: [VariableType.Integer, VariableType.Float],
		yNumericVariable: [VariableType.Integer, VariableType.Float],
		groupingVariable: VariableType.Category
	},
	[AnalysisType.CorrelationsV2]: {
		xVariable: [VariableType.Integer, VariableType.Float],
		yVariable: [VariableType.Integer, VariableType.Float],
		groupVariables: [VariableType.Category, VariableType.Integer, VariableType.Unique]
	},
	[AnalysisType.PlotNumeric]: {
		categoryVariable: VariableType.Category,
		numericVariable: [VariableType.Integer, VariableType.Float],
		groupingVariable: VariableType.Category
	},
	[AnalysisType.PlotNumericV2]: {
		categoryVariable: VariableType.Category,
		numericVariable: [VariableType.Integer, VariableType.Float],
		groupingVariable: VariableType.Category
	},
	[AnalysisType.DensityPlot]: {
		numericVariable: [VariableType.Integer, VariableType.Float],
		groupingVariable: VariableType.Category
	},
	[AnalysisType.DensityPlotV2]: {
		numericVariable: [VariableType.Integer, VariableType.Float],
		groupVariables: VariableType.Category
	},
	[AnalysisType.TimeCourseV2]: {
		numericVariable: [VariableType.Integer, VariableType.Float],
		timeVariable: [VariableType.Date, VariableType.DateTime],
		groupVariables: [VariableType.Category]
	},
	[AnalysisType.TimeCourseV1]: {
		numericVariable: [VariableType.Integer, VariableType.Float],
		dateVariable: [VariableType.Date, VariableType.DateTime],
		groupingVariable: VariableType.Category
	},
	[AnalysisType.ComparePaired]: {
		numericVariableOne: [VariableType.Integer, VariableType.Float],
		numericVariableTwo: [VariableType.Integer, VariableType.Float],
		catVarnameDiffSamples: VariableType.Category,
		firstCategoryValue: VariableType.String,
		secondCategoryValue: VariableType.String,
		groupOne: VariableType.String,
		groupTwo: VariableType.String,
		patientIdentifierVarname: [VariableType.String, VariableType.Integer],
		setName: [VariableType.String],
		testVariable: [VariableType.Integer, VariableType.Float],
		groupVariable: VariableType.Category
	},
	[AnalysisType.LogisticRegression]: {
		independentVariable: [VariableType.Integer, VariableType.Float],
		groupVariable: VariableType.Category,
		dependentVariable: VariableType.Category,
		positiveEvent: [VariableType.String]
	},
	[AnalysisType.LogisticRegressionV2]: {
		xVariable: [VariableType.Integer, VariableType.Float],
		groupVariables: [VariableType.Category],
		yVariable: VariableType.Category,
		outcomes: [VariableType.String]
	}
};
